/**
 * Ships PHI-safe browser warnings and errors to the backend so they land in
 * CloudWatch instead of dying in a console on one user's laptop.
 *
 * Two prod incidents motivated this. On 2026-09-25 a hosting misconfiguration
 * blanked the app for ~37 h; on 2026-09-28 a CORS misconfiguration blocked
 * every API call before it left the browser, so no server log or metric moved.
 * In both cases the evidence existed only in a DevTools console.
 *
 * Reporting is strictly best-effort. It never throws, never retries forever,
 * and never changes app behaviour: a telemetry outage must not become a user
 * outage.
 */

import { API_BASE_URL } from "../constants";
import { getActiveClinicId } from "./active-clinic";

export type ClientLogLevel = "WARN" | "ERROR";

// Closed sets mirroring the Literals in app/api/v1/endpoints/client_errors.py.
// The server rejects the WHOLE batch on an unknown value, so these must change
// together with the backend, never ahead of it.
export type ClientFlow =
  | "app_load"
  | "auth"
  | "access"
  | "patients"
  | "appointments"
  | "consultation"
  | "allergy"
  | "sync"
  | "subscription"
  | "clinic_money"
  | "notify"
  | "reports"
  | "admin";

export type ClientErrorCode =
  | "APP_LOAD_FAILED"
  | "CHUNK_LOAD_FAILED"
  | "NETWORK_BLOCKED"
  | "COGNITO_SIGNIN_FAILED"
  | "COGNITO_SIGNUP_FAILED"
  | "ALLERGY_CHECK_UNAVAILABLE"
  | "JS_CRASH";

export interface ClientLogEntry {
  level: ClientLogLevel;
  message: string;
  error_name?: string;
  path?: string;
  request_id?: string;
  app_version?: string;
  flow?: ClientFlow;
  code?: ClientErrorCode;
  /** The clinic the browser believes it is in. The server logs it as a claim. */
  clinic_id?: string;
}

export interface ClientLogOptions {
  flow?: ClientFlow;
  code?: ClientErrorCode;
  /** Defaults to the active clinic from utils/active-clinic. */
  clinicId?: string | null;
}

// Mirrors the server-side caps in app/api/v1/endpoints/client_errors.py.
// Truncating here keeps a long message from failing validation and losing the
// whole batch, including the other entries in it.
const MAX_MESSAGE = 200;
const MAX_ERROR_NAME = 100;
const MAX_PATH = 200;
const MAX_BATCH = 10;
const QUEUE_LIMIT = 50;
const FLUSH_INTERVAL_MS = 5000;

const ENDPOINT = `${API_BASE_URL}/client-errors`;

let queue: ClientLogEntry[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
// Re-entrancy guard. Without it, a failure inside the reporter logs an error,
// which enqueues another report, which fails again: an infinite loop that
// turns one broken endpoint into a busy loop in every user's browser.
let reporting = false;

const truncate = (s: string, n: number): string =>
  s.length > n ? s.slice(0, n) : s;

// The server types clinic_id as a UUID, so anything else would fail validation
// and drop every other entry in the batch with it.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function safeClinicId(explicit?: string | null): string | undefined {
  const id = explicit !== undefined ? explicit : getActiveClinicId();
  return id && UUID_RE.test(id) ? id : undefined;
}

/** Path only, never the query string: search terms routinely contain PHI. */
function safePath(): string | undefined {
  try {
    return truncate(window.location.pathname, MAX_PATH);
  } catch {
    return undefined;
  }
}

function send(entries: ClientLogEntry[]): void {
  if (!entries.length) return;
  const payload = JSON.stringify({ entries });

  // keepalive, not sendBeacon. Both survive page unload, but sendBeacon always
  // sends in credentials mode "include", and a credentialed cross-origin
  // request makes the gateway's Access-Control-Allow-Headers "*" match
  // literally instead of as a wildcard (Fetch spec). That silently blocked
  // every report on staging. fetch defaults to credentials "same-origin",
  // which is uncredentialed cross-origin, so the wildcard applies normally.
  try {
    void fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    }).catch(() => {
      /* telemetry is best-effort; swallow */
    });
  } catch {
    /* swallow */
  }
}

export function flushClientErrors(): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  const batch = queue.slice(0, MAX_BATCH);
  queue = queue.slice(MAX_BATCH);
  reporting = true;
  try {
    send(batch);
  } finally {
    reporting = false;
  }
  if (queue.length && !timer) {
    timer = setTimeout(flushClientErrors, FLUSH_INTERVAL_MS);
  }
}

export function reportClientLog(
  level: ClientLogLevel,
  message: string,
  errorName?: string,
  requestId?: string,
  options: ClientLogOptions = {},
): void {
  if (reporting) return;
  // Drop oldest rather than newest: during a storm the first errors are the
  // ones that explain the cause, but an unbounded queue is a memory leak.
  if (queue.length >= QUEUE_LIMIT) queue.shift();

  queue.push({
    level,
    message: truncate(message, MAX_MESSAGE),
    error_name: errorName ? truncate(errorName, MAX_ERROR_NAME) : undefined,
    path: safePath(),
    request_id: requestId,
    app_version: process.env.REACT_APP_VERSION,
    flow: options.flow,
    code: options.code,
    clinic_id: safeClinicId(options.clinicId),
  });

  if (!timer) timer = setTimeout(flushClientErrors, FLUSH_INTERVAL_MS);
}

export function installClientErrorFlush(): void {
  if (typeof window === "undefined") return;
  // pagehide beats beforeunload on mobile Safari, which often never fires the latter.
  window.addEventListener("pagehide", flushClientErrors);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushClientErrors();
  });
}
